import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../lib/api';

const GEO_KEY = 'dealgo_geo';
const GeoContext = createContext(null);

const toRad = (d) => (d * Math.PI) / 180;
export const haversineKm = (a, b) => {
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

export const formatKm = (km) => (km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`);

export const GeoProvider = ({ children }) => {
  const [coords, setCoords] = useState(() => { try { return JSON.parse(localStorage.getItem(GEO_KEY)); } catch { return null; } });
  const [status, setStatus] = useState(coords ? 'ready' : 'idle');
  const [brands, setBrands] = useState(null);

  const locate = useCallback(() => new Promise((resolve) => {
    if (!navigator.geolocation) { setStatus('unsupported'); return resolve(null); }
    setStatus('locating');
    navigator.geolocation.getCurrentPosition(
      ({ coords: c }) => {
        const me = { lat: c.latitude, lng: c.longitude };
        localStorage.setItem(GEO_KEY, JSON.stringify(me));
        setCoords(me); setStatus('ready'); resolve(me);
      },
      () => { setStatus('denied'); resolve(null); },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 },
    );
  }), []);

  useEffect(() => {
    if (!coords || brands) return;
    api.get('/brands').then((r) => setBrands(r.data)).catch(() => setBrands([]));
  }, [coords, brands]);

  const distances = useMemo(() => {
    if (!coords || !brands) return {};
    const out = {};
    brands.forEach((b) => {
      const locs = (b.locations || []).filter((l) => l.lat != null && l.lng != null);
      if (locs.length) out[b.id] = Math.min(...locs.map((l) => haversineKm(coords, l)));
    });
    return out;
  }, [coords, brands]);

  const distanceFor = useCallback((brandId) => distances[brandId], [distances]);
  const sortByDistance = useCallback((list) => [...list].sort((a, b) => (distances[a.brand_id] ?? Infinity) - (distances[b.brand_id] ?? Infinity)), [distances]);

  return <GeoContext.Provider value={{ coords, status, locate, distanceFor, sortByDistance, ready: !!coords && !!brands }}>{children}</GeoContext.Provider>;
};

export const useGeo = () => useContext(GeoContext);
